import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { listConcepts, listLanes } from './api';
import GuidedIntake from './GuidedIntake';
import ConceptWorkspace from './ConceptWorkspace';
import ConceptCard from './ConceptCard';
import ConceptsList from './ConceptsList';

// Lane colours only. The lane-to-status mapping lives on the server
// (TrainingConcept::LANES): a client-side copy is what let approved_for_scheduling belong
// to no lane at all, so a live class that reached scheduling rendered nowhere.
const LANE_COLORS = {
  drafting: '#94a3b8',
  match: '#0ea5e9',
  review: '#3b82f6',
  approved: '#22c55e',
  sale: '#f97316',
  production: '#14b8a6',
  closed: '#64748b',
};

// How many cards a lane draws before it stops and says how many it is hiding. The board is
// pinned to one viewport, so this is about render cost, not height - and a cap that names
// what it withheld is fine, unlike the silent 200-row cap this replaces.
const LANE_CARD_CAP = 25;

// One page holds enough to fill every lane past its cap. Beyond that the List view is the
// right tool, and the header says so rather than the board quietly dropping rows.
const BOARD_PAGE_SIZE = 200;

export default function ConceptsPipeline() {
  const [view, setView] = useState('board');       // board | list | intake | workspace
  const [activeId, setActiveId] = useState(null);
  const [returnTo, setReturnTo] = useState('board');
  const [mine, setMine] = useState(false);
  const [q, setQ] = useState('');
  const [laneFilter, setLaneFilter] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [sortKey, setSortKey] = useState('updated_at');
  const [sortDir, setSortDir] = useState('desc');

  const isList = view === 'list';
  const showing = view === 'board' || isList;

  const { data: laneData } = useQuery({ queryKey: ['training-lanes'], queryFn: listLanes });
  const lanes = laneData?.lanes || [];

  const params = isList
    ? { mine: mine ? 1 : undefined, q: q || undefined, lane: laneFilter || undefined,
        page, per_page: perPage, sort: sortKey, dir: sortDir }
    : { mine: mine ? 1 : undefined, q: q || undefined, per_page: BOARD_PAGE_SIZE };

  const { data, isLoading } = useQuery({
    queryKey: ['training-concepts', params],
    queryFn: () => listConcepts(params),
    enabled: showing,
  });

  const open = (id) => { setReturnTo(isList ? 'list' : 'board'); setActiveId(id); setView('workspace'); };

  // Tabs scroll their lane into view and flag it briefly. The board is a horizontal
  // scroller, so with seven lanes the ones on the right are off screen - the tab row is
  // how you see that a stage exists and has work in it without scrolling to find out.
  const laneRefs = React.useRef({});
  const [flashLane, setFlashLane] = React.useState(null);
  const focusLane = (laneId) => {
    const el = laneRefs.current[laneId];
    if (el) el.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    setFlashLane(laneId);
  };
  // Clear the flag after the highlight has been seen, so it reads as a pointer rather than
  // a selected state the board does not otherwise have.
  React.useEffect(() => {
    if (!flashLane) return undefined;
    const t = setTimeout(() => setFlashLane(null), 1600);
    return () => clearTimeout(t);
  }, [flashLane]);

  if (view === 'intake')
    return <GuidedIntake onGenerated={(id) => { setActiveId(id); setReturnTo('board'); setView('workspace'); }}
      onCancel={() => setView('board')} />;
  if (view === 'workspace')
    return <ConceptWorkspace conceptId={activeId} onBack={() => setView(returnTo)} />;

  const concepts = data?.concepts || [];
  const counts = data?.counts || {};
  const total = data?.total ?? concepts.length;

  // Real per-status totals from the server, so a lane header is the whole truth even when
  // its cards are capped. This used to count what happened to survive the fetch.
  const laneTotal = (lane) => (lane.statuses || []).reduce((n, s) => n + (counts[s] || 0), 0);
  const laneCards = (lane) => concepts.filter((c) => (lane.statuses || []).includes(c.status));

  const jumpToLane = (laneId) => { setLaneFilter(laneId); setPage(1); setView('list'); };

  return (
    <div className="flex flex-col h-[calc(100vh-190px)] min-h-[520px]">
      {/* Controls */}
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap shrink-0">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex rounded-full border border-gray-300 dark:border-gray-600 overflow-hidden">
            {[['board', 'Board'], ['list', 'List']].map(([id, label]) => (
              <button key={id} onClick={() => { setView(id); setPage(1); }}
                className={`text-xs px-3 py-1.5 ${view === id
                  ? 'bg-blue-50 text-blue-600 dark:bg-blue-900/40 dark:text-blue-300'
                  : 'text-gray-500'}`}>
                {label}
              </button>
            ))}
          </div>
          <button onClick={() => setMine(!mine)}
            className={`text-xs px-3 py-1.5 rounded-full border ${mine
              ? 'bg-blue-50 border-blue-400 text-blue-600 dark:bg-blue-900/40 dark:text-blue-300'
              : 'border-gray-300 dark:border-gray-600 text-gray-500'}`}>
            ⚑ My reviews
          </button>
          <input
            value={q}
            onChange={(e) => { setQ(e.target.value); setPage(1); }}
            placeholder="Search title or ID…"
            className="text-xs px-3 py-1.5 rounded-full border border-gray-300 dark:border-gray-600 bg-transparent dark:bg-gray-800 w-52"
          />
          {isList && (
            <select value={laneFilter} onChange={(e) => { setLaneFilter(e.target.value); setPage(1); }}
              className="text-xs px-2 py-1.5 rounded-full border border-gray-300 dark:border-gray-600 bg-transparent dark:bg-gray-800">
              <option value="">All stages</option>
              {lanes.map((l) => <option key={l.id} value={l.id}>{l.label}</option>)}
            </select>
          )}
          {isList && (
            <select value={perPage} onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }}
              className="text-xs px-2 py-1.5 rounded-full border border-gray-300 dark:border-gray-600 bg-transparent dark:bg-gray-800">
              {[25, 50, 100, 200].map((n) => <option key={n} value={n}>{n} / page</option>)}
            </select>
          )}
        </div>
        <button onClick={() => setView('intake')}
          className="text-xs font-bold px-4 py-2 rounded-lg bg-orange-500 text-white">
          + New concept
        </button>
      </div>

      {isLoading && <div className="text-xs text-gray-400">Loading concepts…</div>}

      {isList ? (
        <div className="flex-1 min-h-0 overflow-y-auto">
          <ConceptsList
            concepts={concepts} total={total} page={page} perPage={perPage}
            truncated={Boolean(data?.truncated)} lanes={lanes}
            sortKey={sortKey} sortDir={sortDir}
            onSort={(k, d) => { setSortKey(k); setSortDir(d); setPage(1); }}
            onPage={(p) => setPage(Math.max(1, p))}
            onOpen={open}
          />
        </div>
      ) : (
        <>
          {/* Every stage, with its count, whether or not it holds anything. Answers "which
              stages have work" at a glance, and gets you to one without hunting along a
              horizontal scroller. A stage with nothing in it is dimmed rather than absent:
              knowing a stage is empty is information too. */}
          <div className="flex items-center gap-1.5 mb-3 overflow-x-auto pb-1 shrink-0">
            {lanes.map((lane) => {
              const n = laneTotal(lane);
              return (
                <button key={lane.id} onClick={() => focusLane(lane.id)}
                  className={`shrink-0 flex items-center gap-1.5 text-[11px] px-2.5 py-1.5 rounded-full border transition ${
                    flashLane === lane.id
                      ? 'border-blue-400 bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                      : n > 0
                        ? 'border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700/50'
                        : 'border-gray-200 dark:border-gray-700 text-gray-400 dark:text-gray-500'}`}>
                  <span className="w-2 h-2 rounded-full shrink-0"
                    style={{ background: LANE_COLORS[lane.id] || '#94a3b8', opacity: n > 0 ? 1 : 0.35 }} />
                  <span className="whitespace-nowrap">{lane.label}</span>
                  <span className={`px-1.5 rounded-full text-[10px] font-semibold ${n > 0
                    ? 'bg-gray-200 dark:bg-gray-700'
                    : 'bg-transparent'}`}>{n}</span>
                </button>
              );
            })}
          </div>

          {/* One horizontal row, pinned to the viewport, each lane scrolling on its own.
              The previous 4-column grid wrapped 7 lanes onto two rows of 62vh, so the page
              scrolled AND every lane scrolled inside it - nested scrollbars with only six
              concepts on the board. Card count no longer affects page height at all. */}
          <div className="flex-1 min-h-0 flex gap-3 overflow-x-auto pb-2">
            {lanes.map((lane) => {
              const cards = laneCards(lane);
              const totalForLane = laneTotal(lane);
              const hidden = Math.max(0, totalForLane - cards.length);
              const capped = cards.slice(0, LANE_CARD_CAP);
              const notDrawn = hidden + Math.max(0, cards.length - capped.length);
              const empty = totalForLane === 0;

              return (
                <div key={lane.id}
                  ref={(el) => { laneRefs.current[lane.id] = el; }}
                  className={`shrink-0 flex flex-col rounded-2xl p-3 border transition ${
                    // An empty lane narrows but keeps its position: lane order is the
                    // board's mental map, so hiding empty lanes would shuffle the rest.
                    empty ? 'w-[150px] bg-transparent border-dashed border-gray-200 dark:border-gray-700'
                          : 'w-[290px] bg-gray-50 dark:bg-gray-800/30 border-gray-200 dark:border-gray-700'} ${
                    flashLane === lane.id ? 'ring-2 ring-blue-400' : ''}`}>
                  <div className="flex items-center justify-between mb-3 px-1 shrink-0">
                    <span className={`text-xs font-bold flex items-center gap-2 min-w-0 ${
                      empty ? 'text-gray-400 dark:text-gray-500' : ''}`}>
                      <span className="w-2 h-2 rounded-full shrink-0"
                        style={{ background: LANE_COLORS[lane.id] || '#94a3b8', opacity: empty ? 0.35 : 1 }} />
                      <span className="truncate">{lane.label}</span>
                    </span>
                    {!empty && <span className="text-[11px] text-gray-400 shrink-0">{totalForLane}</span>}
                  </div>

                  <div className="flex-1 min-h-0 overflow-y-auto pr-0.5">
                    {/* A bare "—" read as a broken card. An empty lane says so in words,
                        centred and dimmed, so it looks like a deliberately quiet stage
                        rather than something that failed to render. */}
                    {empty && (
                      <div className="h-full flex items-center justify-center px-2">
                        <span className="text-[10px] text-gray-400 dark:text-gray-500 text-center leading-snug">
                          Nothing<br />in this stage
                        </span>
                      </div>
                    )}
                    {capped.map((c) => <ConceptCard key={c.id} concept={c} onOpen={open} />)}
                    {notDrawn > 0 && (
                      // Names what it is withholding, and hands off to the tool that can
                      // show it. The cap this replaces said nothing at all.
                      <button onClick={() => jumpToLane(lane.id)}
                        className="w-full text-[11px] text-blue-600 dark:text-blue-400 py-2 hover:underline">
                        +{notDrawn} more in this stage →
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {data?.truncated && (
            <div className="text-[11px] text-gray-500 mt-2 shrink-0">
              {total} concepts match. The board draws the most recent;{' '}
              <button onClick={() => { setView('list'); setPage(1); }}
                className="text-blue-600 dark:text-blue-400 hover:underline">open the List view</button>{' '}
              to see all of them.
            </div>
          )}
        </>
      )}
    </div>
  );
}
